<template>
    <div class="container">
        <div v-if="isLoading" class="loading-container">
            <BaseLoading />
        </div>
        <div v-else class="manage-admins">
            <ButtonBack></ButtonBack>

            <div class="box" :class="{ 'hide-phone-column': shouldHidePhoneColumn }">
                <h1 class="h2">Manage Admins</h1>
                <div class="admin-header">
                    <InputSwitch
                        v-if="isCurrentUserStaff"
                        v-model="isMfaFeatureEnabled"
                        label="Enable MFA"
                        @update:modelValue="handleMfaToggle"
                        :disabled="isMfaLoading || !isCurrentUserStaff"
                        aria-label="Toggle Multi-Factor Authentication for all administrators"
                    />
                    <div class="admin-actions">
                        <BaseInputText
                            v-model="searchText"
                            label="Search Admins"
                            hideLabel
                            placeholder="Search Admins"
                            class="search-input"
                        />
                        <BaseButton
                            class="button add-admin"
                            @click="onClickAddAdmin"
                            label="Add New"
                        />
                        <BaseButton
                            class="button remove-admin"
                            variant="secondary"
                            @click="handleRemoveSelectedAdmins"
                            :disabled="selectedAdmins.length === 0"
                            label="Remove"
                        />
                    </div>
                </div>
                <BaseTable
                    :value="admins.list"
                    :columns="baseTableColumns"
                    :loading="isAdminListLoading"
                    :loadingNextPage="admins.loadingNextPage"
                    :displayLoadMoreButton="admins.pagination.next != null"
                    empty-state-message="No admins found."
                    @lazy-load="loadMoreAdmins"
                >
                    <template #selectedColumn="{ data }">
                        <BaseCheckbox
                            :modelValue="
                                Boolean(selectedAdmins.find((item) => item.id === data.id))
                            "
                            @update:modelValue="(newVal) => toggleAdminSelection(data, newVal)"
                            label="Select admin"
                            hideLabel
                        />
                    </template>
                    <template #nameColumn="{ data }">
                        <div
                            v-if="editingAdmin && editingAdminId === data.id && isCurrentUserStaff"
                            class="name-inputs"
                        >
                            <BaseInputText
                                v-model="currentFirstName"
                                label="First Name"
                                hideLabel
                                placeholder="First Name"
                                class="name-input"
                                :disabled="!canEditNameEmail"
                            />
                            <BaseInputText
                                v-model="currentLastName"
                                label="Last Name"
                                hideLabel
                                placeholder="Last Name"
                                class="name-input"
                                :disabled="!canEditNameEmail"
                            />
                        </div>
                        <div v-else class="name-display">
                            <span>{{ formatDisplayName(data) }}</span>
                            <BaseLoading
                                v-if="data.sendingInvite"
                                class="inline-spinner"
                                size="sm"
                            />
                            <BaseButton
                                v-if="!data.firstName && !data.sendingInvite"
                                class="button resend-invite"
                                @click="handleResendInvite(data)"
                                label="Resend Invite"
                            />
                        </div>
                    </template>
                    <template #emailColumn="{ data }">
                        <div
                            v-if="editingAdmin && editingAdminId === data.id && isCurrentUserStaff"
                        >
                            <BaseInputText
                                v-model="currentEmail"
                                label="Email"
                                hideLabel
                                placeholder="Enter email"
                                class="email-input"
                                :disabled="!canEditNameEmail"
                            />
                        </div>
                        <span v-else>{{ data.email }}</span>
                    </template>
                    <template #phoneNumberColumn="{ data }">
                        <div v-if="editingAdmin && editingAdminId === data.id">
                            <BaseInputText
                                v-model="currentPhoneNumber"
                                label="Phone Number"
                                hideLabel
                                placeholder="e.g. 5551234567"
                                class="phone-input"
                                :disabled="!canEditPhoneNumber"
                            />
                        </div>
                        <div v-else>
                            <span>
                                {{ formatDisplayPhoneNumber(data) }}
                            </span>
                        </div>
                    </template>
                    <template #permissionColumn="{ data }">
                        <div v-if="editingAdmin && editingAdminId === data.id">
                            <BaseSelect
                                v-model="currentPermission"
                                label="Permission"
                                hideLabel
                                :options="[
                                    { label: 'UNLIMITED', value: 'UNLIMITED' },
                                    { label: 'LIMITED', value: 'LIMITED' }
                                ]"
                                class="permission-dropdown"
                                appendTo="body"
                                :disabled="!canEditPermission"
                            />
                        </div>
                        <div v-else>
                            <span>{{ adminDisplayValue(data.adminAccount.type) }}</span>
                        </div>
                    </template>
                    <template #permissionsColumn="{ data }">
                        <div v-if="editingAdmin && editingAdminId === data.id">
                            <BaseCheckbox
                                label="KB Access"
                                :modelValue="
                                    currentPermission === 'UNLIMITED' ? true : currentKbAccess
                                "
                                :disabled="currentPermission === 'UNLIMITED'"
                                @update:modelValue="currentKbAccess = $event"
                            />
                            <BaseCheckbox
                                label="Can Upload Files"
                                :modelValue="currentPermission === 'UNLIMITED' ? true : currentStudentUploadAccess"
                                :disabled="currentPermission === 'UNLIMITED'"
                                @update:modelValue="currentStudentUploadAccess = $event"
                            />
                        </div>
                        <div v-else class="permissions-access-display">
                            <div class="permission-item">
                                <BaseIcon :icon="data.adminAccount.canAccessKb ? 'check' : 'times'" />
                                <span>Knowledge Base Access</span>
                            </div>
                            <div class="permission-item">
                                <BaseIcon :icon="data.adminAccount.canAccessStudentUploads ? 'check' : 'times'" />
                                <span>Can Upload Files</span>
                            </div>
                        </div>
                    </template>
                    <template #actionsColumn="{ data }">
                        <div
                            v-if="editingAdmin && editingAdminId === data.id"
                            class="action-buttons-edit"
                        >
                            <BaseButton
                                class="button save"
                                @click="saveAdminChanges(data)"
                                label="Save"
                            />
                            <BaseButton
                                class="button cancel"
                                variant="secondary"
                                @click="cancelAdminChanges"
                                label="Cancel"
                            />
                        </div>
                        <div v-else class="action-menu-container">
                            <BaseButtonMenu :items="getAdminMenuItems(data)" />
                        </div>
                    </template>
                </BaseTable>
            </div>
        </div>

        <ModalResetAdminPassword />
    </div>
</template>

<script setup>
import { computed, onMounted, getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router';

// Composables
import { useAdminManagement } from '@/views/AdminSettings/composables/useAdminManagement';
import { useMfaToggle } from '@/views/AdminSettings/composables/useMfaToggle';
import { useAdminInlineEdit } from '@/views/AdminSettings/composables/useAdminInlineEdit';

// Components
import ButtonBack from '@/components-deprecated/ButtonBack';
import InputSwitch from '@/components-deprecated/global/v2/InputSwitch.vue';
import ModalResetAdminPassword from '@/views/modals/ModalResetAdminPassword.vue';
import Alerts from '@/lib/alerts';

// UI-Core Components
import {
    BaseTable,
    BaseCheckbox,
    BaseSelect,
    BaseInputText,
    BaseLoading,
    BaseIcon,
    BaseButton,
    BaseButtonMenu
} from '@edsights/ui-core';
import { useRootStore } from '@/stores';

// --- Component Setup ---
const store = useRootStore();
const router = useRouter();
const { proxy } = getCurrentInstance();

// --- Computed Properties ---
const user = computed(() => store.user);
const schoolId = computed(() => store.user.school);
const staffSchoolViewEnabled = computed(() => store.staffSchoolViewEnabled);
const isCurrentUserStaff = computed(() => store.user.isStaff);

// --- Instantiate Composables ---

const {
    admins,
    isLoading: isAdminListLoading,
    searchText,
    selectedAdmins,
    loadMoreAdmins,
    refreshAdmins,
    setupAdminSearch,
    resendInvite,
    removeAdmins,
    toggleAdminSelection,
    formatDisplayName,
    formatDisplayPhoneNumber
} = useAdminManagement(staffSchoolViewEnabled, schoolId, user);

const {
    isMfaFeatureEnabled,
    isLoading: isMfaLoading,
    isInitialLoading: isMfaInitialLoading,
    fetchInitialMfaState,
    toggleMfaFeature,
    validateAllAdminsHavePhone
} = useMfaToggle(
    schoolId,
    computed(() => admins.list)
);

const {
    editingAdmin,
    editingAdminId,
    currentPermission,
    currentKbAccess,
    currentStudentUploadAccess,
    currentFirstName,
    currentLastName,
    currentEmail,
    currentPhoneNumber,
    canEditNameEmail,
    canEditPhoneNumber,
    canEditPermission,
    saveAdminChanges,
    cancelAdminChanges,
    adminDisplayValue,
    getAdminMenuItems
} = useAdminInlineEdit(
    user,
    refreshAdmins,
    (admin) => proxy.$modal.show('modal-reset-admin-password', { admin }),
    (admin) => router.push({ name: 'ManageAdminTags', params: { adminId: admin.id } }),
    isMfaFeatureEnabled
);

// --- Combined Loading State ---
const isLoading = computed(() => isAdminListLoading.value || isMfaInitialLoading.value);

// --- Computed property to determine if phone column should be hidden ---
const shouldHidePhoneColumn = computed(() => {
    return !isCurrentUserStaff.value && !isMfaFeatureEnabled.value;
});

// --- Table Columns ---
const baseTableColumns = computed(() => {
    const columns = [
        { field: 'selected', header: '', slotName: 'selectedColumn' },
        { field: 'name', header: 'Name', slotName: 'nameColumn' },
        { field: 'email', header: 'Email', slotName: 'emailColumn' },
        { field: 'phoneNumber', header: 'Phone Number\n(MFA)', slotName: 'phoneNumberColumn' },
        { field: 'permission', header: 'Permission', slotName: 'permissionColumn' },
        { field: 'permissions', header: 'Permissions', slotName: 'permissionsColumn' },
        { field: 'actions', header: 'Actions', slotName: 'actionsColumn' }
    ];

    if (shouldHidePhoneColumn.value) {
        return columns.filter((col) => col.field !== 'phoneNumber');
    }

    return columns;
});

// --- Component-Specific Actions ---
const onClickAddAdmin = () => {
    router.push({ name: 'InviteCoAdmins' });
};

// --- Handlers Calling Composables (with UI feedback) ---
const handleResendInvite = async (admin) => {
    try {
        const sentToEmail = await resendInvite(admin.id);
        Alerts.alert({
            type: 'success',
            message: `<h2>Resent invite</h2><br /><p>Sent to ${sentToEmail}</p>`,
            timeout: 6000
        });
    } catch (error) {
        console.error('Failed to resend invite (component):', error);
        Alerts.alert({
            type: 'error',
            message:
                "<h2>Problem sending invite</h2><br /><p>Please try again or get in touch with your school's EdSights contact.</p>",
            timeout: 6000
        });
    }
};

const handleRemoveSelectedAdmins = async () => {
    if (!selectedAdmins.value.length) {
        return;
    }

    const numAdmins = selectedAdmins.value.length;
    const confirmMessage =
        numAdmins === 1
            ? `Are you sure you want to remove ${selectedAdmins.value[0].email}?`
            : `Are you sure you want to remove these ${numAdmins} admins?`;

    if (!confirm(confirmMessage)) {
        return;
    }

    const adminIdsToRemove = selectedAdmins.value.map((admin) => admin.id);
    const result = await removeAdmins(adminIdsToRemove);

    if (result.success) {
        let successMessage =
            result.message || `${result.numRemoved} admin(s) removed successfully.`;
        if (result.selfDeactivationAttempted && result.numRemoved < numAdmins) {
            successMessage += ` You cannot deactivate yourself.`;
        }
        if (result.selfDeactivationAttempted && result.numRemoved === 0) {
            Alerts.alert({
                type: 'info',
                message: `You cannot deactivate yourself. No other admins were selected for removal.`,
                timeout: 5000
            });
        } else {
            Alerts.alert({
                type: 'success',
                message: successMessage,
                timeout: 5000
            });
        }
    } else {
        console.error('Failed to remove admins (component):', result.error);
        Alerts.alert({
            type: 'error',
            message: `Failed to remove selected admin(s). Please try again.`,
            timeout: 5000
        });
    }
};

const handleMfaToggle = async (newValue) => {
    if (newValue === true) {
        const validation = validateAllAdminsHavePhone();
        if (!validation.isValid) {
            let errorMessage;
            if (validation.count > 5) {
                errorMessage = `Cannot enable MFA. ${validation.count} admins are missing a valid 10-digit phone number. Please check the admin list, update their profiles, and try again.`;
            } else {
                const emails = validation.emails.join(', ');
                errorMessage = `Cannot enable MFA. The following admins are missing a valid 10-digit phone number: ${emails}. Please update their profiles and try again.`;
            }
            Alerts.alert({
                type: 'error',
                message: errorMessage,
                timeout: 8000
            });
            isMfaFeatureEnabled.value = false;
            return;
        }
    }

    const result = await toggleMfaFeature(newValue);

    if (result.success) {
        Alerts.alert({
            type: 'success',
            message: `MFA has been successfully ${newValue ? 'enabled' : 'disabled'} for all admins.`,
            timeout: 5000
        });
    } else {
        Alerts.alert({
            type: 'error',
            message:
                'Whoops, something went wrong while updating the MFA setting. Please try again.',
            timeout: 5000
        });
    }
};

// --- Lifecycle Hooks ---
onMounted(() => {
    const fetchInitialData = async () => {
        setupAdminSearch();

        try {
            await refreshAdmins();
            const mfaFetchSuccess = await fetchInitialMfaState();
            if (!mfaFetchSuccess) {
                Alerts.alert({
                    type: 'error',
                    message:
                        'Whoops, something went wrong fetching the MFA status. Please try again.',
                    timeout: 5000
                });
            }
        } catch (error) {
            console.error('Error fetching initial data:', error);
            Alerts.alert({
                type: 'error',
                message: 'Failed to load initial admin data. Please try again.',
                timeout: 5000
            });
        }
    };
    fetchInitialData();
});
</script>

<style lang="scss" scoped>
@import '~@/styles/mixins/buttons';

// --- General Layout ---
.loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    width: 100%;
}

.manage-admins {
    width: 100%;
}

.box {
    background-color: #fff;
    border: 1px solid #e3eded;
    box-shadow: 0 5px 5px #f6f9fe;
    border-radius: 6px;
    padding: 25px 20px;
    overflow: hidden;

    &.hide-phone-column {
        // Adjust column widths when phone column is hidden
        // Selected (1)
        :deep(.ui-core-base-table thead th:nth-child(1)),
        :deep(.ui-core-base-table tbody td:nth-child(1)) {
            width: 50px;
        }
        // Name (2)
        :deep(.ui-core-base-table thead th:nth-child(2)),
        :deep(.ui-core-base-table tbody td:nth-child(2)) {
            width: 300px;
        }
        // Email (3)
        :deep(.ui-core-base-table thead th:nth-child(3)),
        :deep(.ui-core-base-table tbody td:nth-child(3)) {
            width: 350px;
        }
        // Permission (4 - was 5)
        :deep(.ui-core-base-table thead th:nth-child(4)),
        :deep(.ui-core-base-table tbody td:nth-child(4)) {
            width: 150px;
        }
        // Permissions (5 - was 6)
        :deep(.ui-core-base-table thead th:nth-child(5)),
        :deep(.ui-core-base-table tbody td:nth-child(5)) {
            width: 220px;
        }
        // Actions (6 - was 7)
        :deep(.ui-core-base-table thead th:nth-child(6)),
        :deep(.ui-core-base-table tbody td:nth-child(6)) {
            width: 175px;
            padding-inline: 0;
        }

        // Adjust centering rules when phone column is hidden
        :deep(.ui-core-base-table thead th:nth-child(1)), // Select
        :deep(.ui-core-base-table thead th:nth-child(4)), // Permission (was 5)
        :deep(.ui-core-base-table thead th:nth-child(5)), // Permissions (was 6)
        :deep(.ui-core-base-table thead th:nth-child(6)), // Actions (was 7)
        :deep(.ui-core-base-table tbody td:nth-child(1)),
        :deep(.ui-core-base-table tbody td:nth-child(4)),
        :deep(.ui-core-base-table tbody td:nth-child(5)),
        :deep(.ui-core-base-table tbody td:nth-child(6)) {
            text-align: center;
            justify-items: center;
            vertical-align: middle;
        }
    }
}

// --- Header & Actions ---
.admin-actions {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
    margin-left: auto;
}

.admin-header {
    display: flex;
    align-items: center;
}

// --- Table Styles ---
:deep(.ui-core-base-table thead th) {
    background-color: #f5f7f9;
    white-space: pre-wrap;
    vertical-align: middle;
}

// Manually adjust column widths when phone column IS present
// Selected (1)
:deep(.ui-core-base-table thead th:nth-child(1)),
:deep(.ui-core-base-table tbody td:nth-child(1)) {
    width: 50px;
}
// Name (2)
:deep(.ui-core-base-table thead th:nth-child(2)),
:deep(.ui-core-base-table tbody td:nth-child(2)) {
    width: 260px;
}
// Email (3)
:deep(.ui-core-base-table thead th:nth-child(3)),
:deep(.ui-core-base-table tbody td:nth-child(3)) {
    width: 310px;
}
// Phone (4)
:deep(.ui-core-base-table thead th:nth-child(4)),
:deep(.ui-core-base-table tbody td:nth-child(4)) {
    width: 150px;
}
// Permission (5)
:deep(.ui-core-base-table thead th:nth-child(5)),
:deep(.ui-core-base-table tbody td:nth-child(5)) {
    width: 150px;
}
// Permissions (6)
:deep(.ui-core-base-table thead th:nth-child(6)),
:deep(.ui-core-base-table tbody td:nth-child(6)) {
    width: 220px;
}
// Actions (7)
:deep(.ui-core-base-table thead th:nth-child(7)),
:deep(.ui-core-base-table tbody td:nth-child(7)) {
    width: 175px;
    padding-inline: 0;
}

// Center these columns for aesthetics when phone column IS present
:deep(.ui-core-base-table thead th:nth-child(1)), // Select
:deep(.ui-core-base-table thead th:nth-child(4)), // Phone
:deep(.ui-core-base-table thead th:nth-child(5)), // Permission
:deep(.ui-core-base-table thead th:nth-child(6)), // Permissions
:deep(.ui-core-base-table thead th:nth-child(7)), // Actions
:deep(.ui-core-base-table tbody td:nth-child(1)),
:deep(.ui-core-base-table tbody td:nth-child(4)),
:deep(.ui-core-base-table tbody td:nth-child(5)),
:deep(.ui-core-base-table tbody td:nth-child(6)),
:deep(.ui-core-base-table tbody td:nth-child(7)) {
    text-align: center;
    justify-items: center;
    vertical-align: middle;
}

// --- Inline Editing Input Styles ---
.permissions-access-display {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;

    .permission-item {
        display: flex;
        align-items: center;
        gap: 0.25rem;
    }

    :deep(.pi-check) {
        color: #28a745;
    }
    :deep(.pi-times) {
        color: #dc3545;
    }
}

.name-inputs {
    display: flex;
    gap: 0.5rem;
}

.name-input {
    width: calc(50% - 0.25rem) !important;
}

.email-input {
    width: 22rem !important;
}

.phone-input {
    width: 10rem !important;
}

.permission-dropdown {
    width: 10rem !important;
}

// --- Button & Action Styles ---
.button {
    &.reset-password {
        color: $primary-brand-color;
    }
    &.resend-invite {
        font-size: 11px;
        font-weight: bold;
        padding: 0;
        margin: 0 !important;
    }
}

.action-buttons-edit {
    display: flex;
    gap: 0.5rem;
    width: 100%;
    justify-content: center;

    .button.save,
    .button.cancel {
        :deep(button) {
            min-width: 6rem;
            padding: 0.4rem 0.8rem;
            font-size: 0.8em;
        }
    }
}

.action-menu-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
}

.name-display {
    display: flex;
    align-items: center;
}

.inline-spinner {
    display: inline-block;
    vertical-align: middle;
    margin-left: 4rem;
}
</style>
